import { useEffect } from "react";
import { trackStep, ReservationStep, claritySet } from "./claritySteps";

function getCookieRawValue(name: string): string | null {
    const raw = document.cookie.split('; ').find(c => c.startsWith(`${name}=`));
    return raw ? decodeURIComponent(raw.split('=').slice(1).join('=')) : null;
}

function includesPerformance(categories: any): boolean {
    if (!categories) return false;
    if (Array.isArray(categories)) return categories.includes('performance');
    if (typeof categories === 'object') {
        return !!categories.performance || Object.values(categories).includes('performance');
    }
    if (typeof categories === 'string') {
        // try to handle JSON-encoded strings like '["a","b"]' first
        try {
            const parsed = JSON.parse(categories);
            return includesPerformance(parsed);
        } catch {
            return categories.split(',').map(s => s.trim()).includes('performance');
        }
    }
    return false;
}

export function useClarityStep(step: ReservationStep, extras?: {
    oneway?: boolean; city?: string; start?: string; end?: string; loggedIn?: boolean;
    paymentMethod?: number | undefined; selectedProvider?: string; resId?: string;
    safeDrive?: boolean; resCancelled?: boolean;
}) {
    useEffect(() => {
        if (typeof window !== "undefined" && window.location.hostname === "localhost") return;

        const cookieName = 'CookieScriptConsent';
        const raw = getCookieRawValue(cookieName);
        if (!raw) return;

        let parsed: any;
        try {
            parsed = JSON.parse(raw);
        } catch (e) {return}

        const clarityAllowed = parsed?.action === 'accept' && includesPerformance(parsed?.categories);
        if (typeof window !== "undefined" && !(window as any).clarity && clarityAllowed) {
            try {
                (window as any).performanceScriptConsent = true;
                // inject a small script node so any script-based loaders can observe it
                const CLARITY_ID = (import.meta.env.VITE_CLARITY as string) || ''; // get env specific id

                const s = document.createElement('script');
                s.type = 'text/javascript';
                s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];
                y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${CLARITY_ID}");`;
                document.head.appendChild(s);
            } catch (e) {
                // ignore parse errors
            }
        } else if (!clarityAllowed){return}

        trackStep(step);
        // Optional context to help filtering (keep values coarse / non-PII)
        if (extras?.city)  claritySet("res_city", extras.city);
        if (extras?.start) claritySet("res_start_hour", extras.start);
        if (extras?.end)  claritySet("res_end_hour", extras.end);
        if (extras?.loggedIn !== undefined) claritySet("logged_in", extras.loggedIn ? "true" : "false");
        if (extras?.paymentMethod) claritySet("res_payment_method", extras.paymentMethod);
        if (extras?.selectedProvider) claritySet("res_selected_provider", extras.selectedProvider);
        claritySet("res_type", !extras?.oneway ? "a2a" : "oneway");
        claritySet("safe_drive", !extras?.safeDrive ? "false" : "true");
        if (extras?.resId) claritySet("res_res_id", extras.resId);
        if (extras?.resCancelled) claritySet("res_cancelled", extras.resCancelled ? "true" : "false");
    }, [step, extras?.city, extras?.start, extras?.end, extras?.loggedIn, extras?.paymentMethod, extras?.oneway,
        extras?.selectedProvider, extras?.resId, extras?.safeDrive, extras?.resCancelled]);
}